import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ContactMail } from '@mui/icons-material';
import { caretakerProfileService } from '../services/caretakerProfileService';

interface ContactCaretakerButtonProps {
  caretakerId: string;
}

const ContactCaretakerButton: React.FC<ContactCaretakerButtonProps> = ({ caretakerId }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleOpen = () => {
    setOpen(true);
    setError(null);
    setSuccess(false);
    setMessage('');
  };

  const handleClose = () => {
    setOpen(false);
    setMessage('');
    setError(null);
    setSuccess(false);
  };

  const handleSendMessage = async () => {
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await caretakerProfileService.contactCaretaker(caretakerId, message);
      setSuccess(true);
      
      // Close dialog after a short delay
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        fullWidth
        startIcon={<ContactMail />}
        onClick={handleOpen}
        sx={{ mb: 1 }}
      >
        Contact Caretaker
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Contact Caretaker</DialogTitle>
        <DialogContent>
          {success ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Your message has been sent successfully!
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Send a message to this caretaker to discuss your care needs, ask questions, or schedule a consultation.
              </Typography>
              
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              
              <TextField
                autoFocus
                margin="dense"
                label="Your Message"
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Hi! I'm interested in your services. I would like to discuss..."
                disabled={loading}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            {success ? 'Close' : 'Cancel'}
          </Button>
          {!success && (
            <Button 
              onClick={handleSendMessage} 
              variant="contained"
              disabled={loading || !message.trim()}
            >
              {loading ? <CircularProgress size={20} /> : 'Send Message'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ContactCaretakerButton;
